import { NotificationBell } from '../../components/notifications/NotificationBell';
import { FeedbackButton } from '../../components/feedback/FeedbackButton';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, Suspense } from 'react';
import {
  LayoutDashboard,
  MapPin,
  Calendar,
  Map as MapIcon,
  Settings,
  GraduationCap,
  Menu,
  X,
  Sun,
  Moon,
  ArrowRight,
  Wifi,
  WifiOff,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useOffline } from '../../hooks/useOffline';
import { describeQueueValue } from './queueReason';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import {
  LITE_HEADER_HEIGHT_VAR,
  usePublishedHeight,
} from '../../hooks/usePublishedHeight';
import { AppVersion } from '../../components/AppVersion';
import { LoadingState } from '../../components/ui/loading-state';

/**
 * Unsent-work banner. Inspections that could not reach the server wait in the
 * IndexedDB sync queue; until 2026-09-04 nothing in Lite showed that, so a
 * failed submit was invisible and was purged after 30 days (audit C1/H6).
 */
export function LiteSyncBadge() {
  const {
    isOnline,
    syncStats,
    syncNow,
    syncProgress,
    failedItems,
    clearFailedItems,
    discardFailedItem,
  } = useOffline();
  const [showAll, setShowAll] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const waiting = syncStats.pending + syncStats.failed;
  if (waiting === 0) return null;
  const syncing = !!syncProgress?.inProgress;
  // LOG-1023-05: say why, as the server said it, and offer a way out — a
  // refused submission used to sit behind "failed to send" with no reason
  // and no control (Robert, 10/23: "how do I clear these old failures out").
  // Chris 10/25: the list opens so each one can be dropped on its own, and
  // the confirm is drawn in the bar — a browser dialog does not show in
  // every home-screen app.
  const describe = (item: (typeof failedItems)[number]) =>
    `${item.label ? `${describeQueueValue(item.label)}: ` : ''}${describeQueueValue(item.error)}`.trim();
  const firstFailed = failedItems[0];
  const reason = firstFailed ? describe(firstFailed) : '';
  const tone =
    syncStats.failed > 0
      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200';
  const smallButton =
    'px-3 py-1 rounded-lg text-xs font-semibold border border-current disabled:opacity-60';
  return (
    <div className={`border-b px-4 py-2 text-sm ${tone}`}>
      <div className="flex items-center gap-3">
        <span className="flex-1">
          {syncStats.failed > 0
            ? `${syncStats.failed} inspection${syncStats.failed === 1 ? '' : 's'} failed to send`
            : `${syncStats.pending} inspection${syncStats.pending === 1 ? '' : 's'} waiting to send`}
          {!isOnline && ' \u2014 offline, will send when connected'}
          {reason && !showAll && (
            <span
              className="block text-xs opacity-80"
              data-testid="sync-failed-reason"
            >
              {reason}
              {failedItems.length > 1 && (
                <>
                  {' '}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setShowAll(true)}
                  >
                    (+{failedItems.length - 1} more)
                  </button>
                </>
              )}
            </span>
          )}
        </span>
        {syncStats.failed > 0 && !confirmAll && (
          <button
            type="button"
            onClick={() => setConfirmAll(true)}
            className={smallButton}
          >
            Discard
          </button>
        )}
        {isOnline && !confirmAll && (
          <button
            type="button"
            onClick={() => syncNow()}
            disabled={syncing}
            className={smallButton}
          >
            {syncing ? 'Sending\u2026' : 'Retry now'}
          </button>
        )}
      </div>
      {confirmAll && (
        <div
          className="mt-2 flex items-center gap-3 text-xs"
          data-testid="sync-discard-confirm"
        >
          <span className="flex-1">
            Discard {syncStats.failed} failed submission
            {syncStats.failed === 1 ? '' : 's'}? They will not be sent.
          </span>
          <button
            type="button"
            onClick={() => setConfirmAll(false)}
            className={smallButton}
          >
            Keep
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmAll(false);
              clearFailedItems();
            }}
            className={`${smallButton} bg-red-600 text-white border-red-600`}
          >
            Yes, discard
          </button>
        </div>
      )}
      {showAll && failedItems.length > 0 && (
        <ul className="mt-2 space-y-1 text-xs" data-testid="sync-failed-list">
          {failedItems.map((item) => (
            <li key={item.id} className="flex items-center gap-2">
              <span className="flex-1 min-w-0 break-words">
                {describe(item) || 'Failed to send'}
              </span>
              <button
                type="button"
                onClick={() => discardFailedItem(item.id)}
                aria-label={`Discard ${describeQueueValue(item.label) || 'this submission'}`}
                className="px-2 py-0.5 rounded border border-current font-semibold"
              >
                Discard
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              className="underline"
              onClick={() => setShowAll(false)}
            >
              Hide list
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export function LiteLayout() {
  const location = useLocation();
  // Hide the mobile bottom nav during the inspection flow so it doesn't cover
  // the Generate Report action bar on narrow viewports. Any nav tap would
  // trigger the in-progress blocker modal anyway.
  const hideMobileBottomNav = location.pathname.startsWith('/lite/inspect/');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Anything that sticks under this header (the site ribbon on the inspection
  // form) reads its real height: 64px on a phone, taller with the tab row on
  // a wide screen or a notch inset.
  const headerRef = useRef<HTMLElement>(null);
  usePublishedHeight(headerRef, LITE_HEADER_HEIGHT_VAR);
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const user = useAuthStore((state) => state.user);
  const { signOut } = useAuth();

  // Inspector-only users should not see dashboard link
  // Super admins and admins should always be able to switch to hub
  const isInspectorOnly =
    user?.role === 'inspector' && !user?.secondaryRoles?.length;

  const navigation = [
    { name: 'Dashboard', href: '/lite', exact: true, icon: LayoutDashboard },
    { name: 'Sites', href: '/lite/sites', icon: MapPin },
    { name: 'Schedule', href: '/lite/schedule', icon: Calendar },
    { name: 'Maps', href: '/lite/maps', icon: MapIcon },
  ];

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex flex-col">
      {/* Offline indicator */}
      {!isOnline && (
        <div className="bg-warning-500 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          You're offline. Changes will sync when connected.
        </div>
      )}

      {/* Header - Optimized for outdoor visibility */}
      <header
        ref={headerRef}
        className="bg-white dark:bg-slate-800 shadow-soft sticky top-0 z-40 safe-area-top"
      >
        <div className="px-4 safe-area-inset relative">
          <div className="flex justify-between items-center h-16">
            {/* Logo — sends the inspector back to the Hub/Lite chooser.
                ?force=true bypasses the auto-redirect on /select-mode so the
                screen actually renders instead of bouncing back to /lite. */}
            <Link
              to="/select-mode?force=true"
              className="flex items-center gap-3 min-w-0"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-sm">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-900 dark:text-white">
                  EnviroLite
                </h1>
                <p className="text-2xs text-slate-500 dark:text-slate-400 -mt-0.5 truncate">
                  {user
                    ? `${user.firstName} ${user.lastName} · ${user.role?.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}`
                    : 'Field Inspector'}
                </p>
              </div>
            </Link>

            {/* Right actions — never squeezed; the name on the left truncates instead */}
            <div className="flex items-center gap-1 min-[400px]:gap-2 shrink-0">
              {/* Online status */}
              <div
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                  isOnline
                    ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                    : 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400'
                }`}
              >
                {isOnline ? (
                  <Wifi className="w-3 h-3" />
                ) : (
                  <WifiOff className="w-3 h-3" />
                )}
                <span className="max-[399px]:sr-only">
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>

              {/* Notifications — rain-event and close-out alerts target
                  inspectors, who previously had no surface for them at all */}
              {isOnline && <FeedbackButton context="lite" />}
              {isOnline && <NotificationBell context="lite" />}

              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                aria-label={
                  isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
                }
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Menu button — shown at ALL sizes. It's the only access point to
                  Settings, so hiding it on desktop (md:hidden) left maximized /
                  full-screen windows with no way to reach Settings (#267). */}
              <button
                className="p-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Desktop Navigation - Tab style */}
          <nav className="hidden md:flex -mb-px">
            {navigation.map((item) => {
              const isActive = item.exact
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all
                    ${
                      isActive
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:border-slate-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Menu (all sizes) - Settings, account, Sign Out. The main nav lives
              in the desktop tab bar / mobile bottom nav; this is the "more" menu
              and the only home for Settings, so it must open on desktop too. */}
          {/* Jim 10/30: on a desktop the menu hangs off the button it came from,
              only as wide as it needs; on a phone it still spans the screen. */}
          {mobileMenuOpen && (
            <nav
              data-testid="lite-menu"
              className="py-3 border-t border-slate-200 dark:border-slate-700 space-y-1 md:absolute md:right-4 md:top-full md:mt-1 md:w-72 md:border md:rounded-xl md:bg-white md:dark:bg-slate-800 md:shadow-lg md:z-50 md:px-2"
            >
              {/* Learn: the walkthrough video and the page guides (Chris 10/31) */}
              <Link
                to="/lite/learn"
                data-testid="lite-menu-learn"
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all
                  ${
                    location.pathname.startsWith('/lite/learn')
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }
                `}
              >
                <GraduationCap className="w-5 h-5" />
                Learn
              </Link>

              {/* Settings link */}
              <Link
                to="/lite/settings"
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-all
                  ${
                    location.pathname.startsWith('/lite/settings')
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                  }
                `}
              >
                <Settings className="w-5 h-5" />
                Settings
              </Link>

              {/* Divider */}
              <div className="border-t border-slate-200 dark:border-slate-700 my-2" />

              {/* User info */}
              {user && (
                <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
                  Signed in as {user.firstName} {user.lastName}
                </div>
              )}

              {/* Logout button */}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  signOut();
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full transition-all"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>

              {/* App version + manual update check */}
              <div className="px-4 pt-2">
                <AppVersion />
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content — lite pages are eager; only /lite/settings (shared
          hub SettingsPage) is code-split and can suspend here */}
      <LiteSyncBadge />

      <main className="flex-1 px-4 py-4 safe-area-inset">
        <Suspense fallback={<LoadingState size="lg" />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Bottom Navigation - Mobile only, fixed */}
      {!hideMobileBottomNav && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 safe-area-inset-bottom z-40">
          <div className="flex justify-around items-center h-16 px-2">
            {navigation.slice(0, 5).map((item) => {
              const isActive = item.exact
                ? location.pathname === item.href
                : location.pathname.startsWith(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                  flex flex-col items-center justify-center py-1 px-3 rounded-lg min-w-[60px] transition-all
                  ${
                    isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-slate-500 dark:text-slate-400'
                  }
                `}
                >
                  <Icon
                    className={`w-6 h-6 ${isActive ? 'scale-110' : ''} transition-transform`}
                  />
                  <span className="text-2xs font-medium mt-0.5">
                    {item.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      {/* Footer - Desktop only */}
      <footer className="hidden md:block bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              © 2024 EnviroLite - Field Inspector Mode
              {user && (
                <span className="ml-2 text-slate-400 dark:text-slate-500">
                  ({user.firstName} {user.lastName})
                </span>
              )}
            </p>
            <div className="flex items-center gap-4">
              <AppVersion />
              {/* Only show dashboard link for non-inspector roles */}
              {!isInspectorOnly && (
                <Link
                  to="/dashboard"
                  className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
                >
                  Switch to EnviroHub
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <button
                onClick={signOut}
                className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 font-medium transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Bottom padding for mobile nav (h-16 nav row + the home-indicator inset
          the fixed nav now extends into, so content never hides behind it). */}
      {!hideMobileBottomNav && (
        <div className="md:hidden h-16 safe-area-inset-bottom" />
      )}
    </div>
  );
}
